import {Component, OnInit, Input, ViewChild } from '@angular/core';
import {DialogRef, DialogService} from '@progress/kendo-angular-dialog';
import { FormGroup, FormBuilder } from '@angular/forms';
import { PageSubheaderBarConfig } from '../../../fw/pages/page-config-api';
import { FormService } from '../../../fw/services/form.service';
import { SharedService } from '../../shared/shared.service';
import { AutoUnsubscribe } from '../../../fw/decoractors/AutoUnsubscribe';
import {SettingsCashregService} from "./settings-cashreg.service";
import {DynamicGridComponent} from "../../../fw/dynamic-grids/dynamic-grid.component";
import {ColumnDefinition} from "../../../fw/dynamic-grids/column-definition";
import {DtoArticleBaseItems} from "./models/DtoArticleBaseItems";
import {DtoPriceChange} from "./models/DtoPriceChange";
import {PriceChangeCorrectionComponent} from "./price-change-correction.component";
import {PriceChangePercentageComponent} from "./price-change-percentage.component";
import {ChosenPercentage} from "./models/ChosenPercentage";
import {ReportPreviewComponent} from "../../shared/report-preview.component";

@Component({
    selector: 'article-price-change',
    template: require('./article-price-change.component.html'),
    styles: [require('./article-price-change.component.css')]
})
@AutoUnsubscribe()

export class ArticlePriceChangeComponent implements OnInit {
    @Input() dialog: DialogRef;
    @ViewChild('grid', {static: false}) grid: DynamicGridComponent;
    priceTypes: Array<{ text: string, value: number }> = [
        { text: "Verkoopprijzen incl. BTW", value: 2 },
        { text: "Inkoopprijzen excl. BTW", value: 3 }
    ];
    pageSubheaderBarConfig: PageSubheaderBarConfig;
    baseItems: DtoArticleBaseItems;
    formSettings: FormGroup;
    columnDefinition: ColumnDefinition[] = [
        {
            key: 'index',
            label: 'Nummer',
            type: 'number',
            width: '110px'
        },
        {
            key: 'description',
            label: 'Omschrijving',
            type: 'string',
            width: '*'
        },
        {
            key: 'oldPrice',
            label: 'Huidige prijs',
            type: 'number',
            width: '120px',
            format: '{0:c}'
        },
        {
            key: 'newPrice',
            label: 'Nieuwe prijs',
            type: 'number',
            width: '120px',
            format: '{0:c}',
            enabled: true
        }
    ];
    dtoObject: DtoPriceChange;
    loadingExisitingObject: boolean;

    constructor(private fb: FormBuilder,
                private fs: FormService,
                private ds: SettingsCashregService,
                private ss: SharedService,
                private dialogService: DialogService) { }

    ngOnInit() {
        this.getBaseItems();
        this.pageSubheaderBarConfig = {
            title: 'ARTIKELEN',
            buttonDefinition: [
                {
                    clickEventType: 'print',
                    description: 'PROEFAFDRUK',
                    isSmall: false
                },
                {
                    clickEventType: 'procentuel',
                    description: 'PROCENTUEEL AANPASSEN',
                    isSmall: false
                },
                {
                    clickEventType: 'future',
                    description: 'TOEKOMSTIGE WIJZIGINGEN',
                    isSmall: false
                }
            ]
        };

        this.formSettings = this.fb.group({
            kind: 2,
            supplierId: 0,
            changeDate: this.ss.getCurrentDate()
        });

        this.formSettings.get('kind').valueChanges.subscribe(newValue => {
            if (!this.loadingExisitingObject)
                this.generatePriceChangeLines();
        });

        this.formSettings.get('supplierId').valueChanges.subscribe(newValue => {
            if (!this.loadingExisitingObject)
                this.filterPriceChangeLines(true);
        });

        this.dtoObject = {
          isDone: false,
          kind: 2,
          id: 0,
          changeDate: this.ss.getCurrentDate(),
          priceChangeLines: []
        };

        this.generatePriceChangeLines();
    }

    getBaseItems() {
        this.ds.getArticleBaseItems(false).subscribe(response => {
            this.baseItems = response;
        });
    }

    generatePriceChangeLines() {
        this.ds.generateArticlePriceChangeLines(this.formSettings.get('kind').value).subscribe(response => {
            this.dtoObject.priceChangeLines = response;
            this.filterPriceChangeLines(false);
        });
    }

    filterPriceChangeLines(getChanges: boolean) {
        if (getChanges) {
            this.getGridPrices();
        }

        this.dtoObject.priceChangeLines.forEach(item => {
            item.show = (item.preferredSupplierId == this.formSettings.get('supplierId').value) || (this.formSettings.get('supplierId').value == 0);
        });

        this.grid.showData(this.dtoObject.priceChangeLines.filter(x => x.show));
    }

    btnSubHeaderBarClicked(event) {
        if (event == 'procentuel') {
            this.showProcentuelWindow();
        } else if (event == 'future') {
            this.showCorrectionWindow();
        } else if (event == 'print') {
            this.printPriceChanges();
        }
    }

    printPriceChanges() {
        let result = this.fs.patchFormToDto(this.dtoObject, this.formSettings);
        this.getGridPrices();

        this.ds.saveArticleTempPriceChanges(result).subscribe(res => {
            if (res.hasError) {
                this.ss.message(res.errorMessage);
            } else {
                const dialogRef = this.dialogService.open({
                    content: ReportPreviewComponent
                });

                const dialogContent: ReportPreviewComponent = dialogRef.content.instance;
                dialogContent.dialog = dialogRef;
                dialogContent.reportName = 'PriceChangeList';
                dialogContent.reportParams = [
                    {
                        name: 'Type',
                        value: 'ARTIKELEN'
                    }
                ];
            }
        });
    }

    showCorrectionWindow() {
        const dialogRef = this.dialogService.open({
            content: PriceChangeCorrectionComponent
        });

        const dialogContent: PriceChangeCorrectionComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.type = 'article';
        dialogContent.chosenId
            .subscribe((res: number) => {
                this.ds.getArticlePriceChangeById(res).subscribe(response => {
                    this.dtoObject = response;
                    this.loadingExisitingObject = true;
                    this.fs.patchDtoToForm(this.dtoObject, this.formSettings);
                    this.loadingExisitingObject = false;
                    this.filterPriceChangeLines(false);
                });
            });
        dialogContent.deletedId
            .subscribe((res: number) => {
                if (this.dtoObject.id == res)
                    this.closeWindow(false);
            });
    }

    showProcentuelWindow() {
        const dialogRef = this.dialogService.open({
            content: PriceChangePercentageComponent
        });

        const dialogContent: PriceChangePercentageComponent = dialogRef.content.instance;
        dialogContent.dialog = dialogRef;
        dialogContent.chosenPercentage
            .subscribe((res: ChosenPercentage) => {
                this.dtoObject.priceChangeLines.filter(x => x.show).forEach(item => {
                    item.newPrice = this.ss.roundToCents((item.oldPrice * (100 + res.percentage) / 100), res.round);
                    item.isPercentage = true;
                });
                this.filterPriceChangeLines(false);
            });
    }

    btnCancelClicked(event) {
        this.closeWindow(false);
    }

    btnSaveClicked(event) {
        this.ss.confirm('Weet u zeker dat u de prijswijzigingen wilt doorvoeren?')
            .subscribe((res: boolean) => {
                if (res) {
                    if (this.dtoObject.id > 0) {
                        this.savePriceChanges();
                    } else {
                        this.ds.articleHasFuturePriceChange(this.formSettings.get('kind').value).subscribe(res => {
                            if (res) {
                                this.ss.message('U kunt geen prijzen wijzigen omdat u al een prijswijziging in de toekomst klaar heeft staan. ' +
                                    'Wilt u nu toch prijzen wijzigen? Klik dan op Toekomstige Prijswijziging en verwijder de geagendeerde prijswijziging.');
                            } else {
                                this.savePriceChanges();
                            }
                        });
                    }
                }
            });
    }

    savePriceChanges() {
        let result = this.fs.patchFormToDto(this.dtoObject, this.formSettings);
        this.getGridPrices();
        this.ds.saveArticlePriceChanges(result).subscribe(res => {
            if (res.hasError) {
                this.ss.message(res.errorMessage);
            } else {
                this.closeWindow(result.changeDate == this.ss.getCurrentDate());
            }
        });
    }

    getGridPrices() {
        if (this.grid.getData()) {
            this.grid.getData().forEach(item => {
                if (this.dtoObject.priceChangeLines.find(x => x.articleId == item.articleId))
                    this.dtoObject.priceChangeLines.find(x => x.articleId == item.articleId).newPrice = item.newPrice;
            });
        }
    }

    closeWindow(result: boolean) {
        if (this.dialog) {
            this.dialog.dialog.instance.action.emit(result);
            this.dialog.close();
        }
    }
}



// WEBPACK FOOTER //
// ./node_modules/angular2-template-loader!./Scripts/app/settings/settings-cashreg/article-price-change.component.ts